/**
 * Decision Hooks
 *
 * React Query hooks for decision management with optimistic updates,
 * caching, and real-time synchronization.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import api, {
  type Decision,
  type DecisionStatus,
  type AgentType,
} from '@/lib/api';

// =============================================================================
// Query Keys
// =============================================================================

export const decisionKeys = {
  all: ['decisions'] as const,
  lists: () => [...decisionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...decisionKeys.lists(), filters] as const,
  details: () => [...decisionKeys.all, 'detail'] as const,
  detail: (id: string) => [...decisionKeys.details(), id] as const,
  recent: (limit: number) => [...decisionKeys.all, 'recent', limit] as const,
};

// =============================================================================
// Auth Token Sync
// =============================================================================

export function useApiAuth() {
  const { getAccessToken, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      const token = getAccessToken();
      if (token) {
        api.setToken(token);
      }
    } else {
      api.clearToken();
    }
  }, [isAuthenticated, getAccessToken]);
}

// =============================================================================
// Hooks
// =============================================================================

interface UseDecisionsOptions {
  status?: DecisionStatus;
  agent?: AgentType;
  vertical?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  enabled?: boolean;
}

export function useDecisions(options: UseDecisionsOptions = {}) {
  const { enabled = true, ...params } = options;

  return useQuery({
    queryKey: decisionKeys.list(params),
    queryFn: () => api.getDecisions(params),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useDecision(id: string) {
  return useQuery({
    queryKey: decisionKeys.detail(id),
    queryFn: () => api.getDecision(id),
    enabled: !!id,
  });
}

export function useRecentDecisions(limit = 10) {
  return useQuery({
    queryKey: decisionKeys.recent(limit),
    queryFn: () => api.getRecentDecisions(limit),
    staleTime: 30 * 1000,
  });
}

export function useApproveDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, optionId }: { id: string; optionId: string }) =>
      api.approveDecision(id, optionId),
    onMutate: async ({ id }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: decisionKeys.detail(id) });

      // Snapshot previous value
      const previous = queryClient.getQueryData<Decision>(decisionKeys.detail(id));

      // Optimistically update
      if (previous) {
        queryClient.setQueryData(decisionKeys.detail(id), {
          ...previous,
          status: 'approved' as DecisionStatus,
        });
      }

      return { previous };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(decisionKeys.detail(id), context.previous);
      }
    },
    onSettled: (_data, _error, { id }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: decisionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: decisionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useRejectDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectDecision(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: decisionKeys.detail(id) });
      const previous = queryClient.getQueryData<Decision>(decisionKeys.detail(id));

      if (previous) {
        queryClient.setQueryData(decisionKeys.detail(id), {
          ...previous,
          status: 'rejected' as DecisionStatus,
        });
      }

      return { previous };
    },
    onError: (_err, { id }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(decisionKeys.detail(id), context.previous);
      }
    },
    onSettled: (_data, _error, { id }) => {
      queryClient.invalidateQueries({ queryKey: decisionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: decisionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

// =============================================================================
// Mock Data for Development
// =============================================================================

const MOCK_DECISIONS: Decision[] = [
  {
    id: 'dec-001',
    title: 'Reallocate Sprint Resources to Critical Path',
    description: 'Analysis indicates Project Phoenix is at risk of missing deadline. Recommend shifting 2 engineers from Project Atlas for 3 weeks.',
    agent: 'capacity',
    status: 'pending',
    confidence: 0.92,
    urgency: 'high',
    impact: { efficiency: 23, cost: -15000 },
    options: [
      {
        id: 'opt-1',
        label: 'Full Reallocation',
        description: 'Move both engineers immediately',
        confidence: 0.92,
        pros: ['Maximizes delivery probability', 'Clear ownership'],
        cons: ['Atlas timeline slips 2 weeks', 'Context switching cost'],
        projectedImpact: { efficiency: 23, cost: -15000, timeline: '3 weeks' },
      },
      {
        id: 'opt-2',
        label: 'Partial Support',
        description: 'Move one engineer, other provides 50% support',
        confidence: 0.78,
        pros: ['Balanced approach', 'Maintains Atlas momentum'],
        cons: ['Split focus risk', 'Lower impact on Phoenix'],
        projectedImpact: { efficiency: 15, cost: -8000, timeline: '4 weeks' },
      },
    ],
    recommendedOptionId: 'opt-1',
    reasoning: 'Phoenix has strategic priority and current trajectory shows 67% probability of missing Q1 deadline without intervention.',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    vertical: 'ops',
  },
  {
    id: 'dec-002',
    title: 'Increase LinkedIn Ad Spend by 40%',
    description: 'Attribution analysis shows LinkedIn driving 3.2x higher conversion rate for enterprise segment. ROAS trending upward.',
    agent: 'revenue',
    status: 'pending',
    confidence: 0.87,
    urgency: 'medium',
    impact: { revenue: 125000, cost: 45000 },
    options: [
      {
        id: 'opt-1',
        label: 'Aggressive Scale',
        description: 'Increase budget by 40% immediately',
        confidence: 0.87,
        pros: ['Capture momentum', 'Q1 pipeline boost'],
        cons: ['Higher CAC risk', 'Creative fatigue potential'],
        projectedImpact: { revenue: 125000, cost: 45000, timeline: '30 days' },
      },
      {
        id: 'opt-2',
        label: 'Gradual Increase',
        description: '20% increase with weekly monitoring',
        confidence: 0.82,
        pros: ['Lower risk', 'Better optimization'],
        cons: ['Slower results', 'May miss window'],
        projectedImpact: { revenue: 75000, cost: 25000, timeline: '45 days' },
      },
    ],
    recommendedOptionId: 'opt-1',
    reasoning: 'Current ROAS of 2.8x with 3-week upward trend. Q1 pipeline target requires accelerated acquisition.',
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    vertical: 'marketing',
  },
  {
    id: 'dec-003',
    title: 'Implement Database Connection Pooling',
    description: 'Risk analysis detected increasing connection timeouts. Pattern suggests 85% probability of service degradation within 72 hours.',
    agent: 'risk',
    status: 'approved',
    confidence: 0.94,
    urgency: 'critical',
    impact: { risk: -45, cost: 5000 },
    options: [
      {
        id: 'opt-1',
        label: 'Immediate Implementation',
        description: 'Deploy connection pooling in next maintenance window',
        confidence: 0.94,
        pros: ['Prevents outage', 'Long-term fix'],
        cons: ['Requires testing', '2-hour downtime'],
        projectedImpact: { risk: -45, cost: 5000 },
      },
    ],
    recommendedOptionId: 'opt-1',
    reasoning: 'Connection pool exhaustion pattern detected. Historical data shows similar patterns preceded 4-hour outages.',
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    vertical: 'ops',
  },
  {
    id: 'dec-004',
    title: 'Adjust Player Rotation Strategy',
    description: 'Fatigue analysis indicates key player at 78% energy. Recommend early substitution to preserve for 4th quarter.',
    agent: 'timing',
    status: 'executing',
    confidence: 0.89,
    urgency: 'high',
    impact: { efficiency: 12 },
    options: [
      {
        id: 'opt-1',
        label: 'Substitute Now',
        description: 'Rest player for 6 minutes, return for final push',
        confidence: 0.89,
        pros: ['Fresh legs for crunch time', 'Injury prevention'],
        cons: ['Momentum disruption', 'Scoring dip risk'],
        projectedImpact: { efficiency: 12 },
      },
    ],
    recommendedOptionId: 'opt-1',
    reasoning: 'Win probability model shows 8% boost from rested player in final 5 minutes vs. current trajectory.',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    vertical: 'sports',
  },
  {
    id: 'dec-005',
    title: 'Cross-Team Sprint Synchronization',
    description: 'Coordination agent detected dependency conflicts between 3 teams. Proposes alignment meeting and shared milestone.',
    agent: 'coordination',
    status: 'completed',
    confidence: 0.91,
    urgency: 'medium',
    impact: { efficiency: 18, risk: -20 },
    options: [
      {
        id: 'opt-1',
        label: 'Synchronized Sprint',
        description: 'Align all 3 teams to shared 2-week sprint',
        confidence: 0.91,
        pros: ['Eliminates blockers', 'Clearer handoffs'],
        cons: ['Initial velocity dip', 'Coordination overhead'],
        projectedImpact: { efficiency: 18, risk: -20 },
      },
    ],
    recommendedOptionId: 'opt-1',
    reasoning: 'Graph analysis shows 7 blocking dependencies. Synchronized approach reduces critical path by 5 days.',
    createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    vertical: 'ops',
  },
];

// Override API methods with mock data in development
if (import.meta.env.DEV) {
  api.getRecentDecisions = async (limit = 10) => {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_DECISIONS.slice(0, limit);
  };

  api.getDecisions = async (params) => {
    await new Promise((r) => setTimeout(r, 400));
    let filtered = [...MOCK_DECISIONS];

    if (params?.status) {
      filtered = filtered.filter((d) => d.status === params.status);
    }
    if (params?.agent) {
      filtered = filtered.filter((d) => d.agent === params.agent);
    }

    return {
      items: filtered,
      total: filtered.length,
      page: params?.page || 1,
      pageSize: params?.pageSize || 10,
      hasMore: false,
    };
  };

  api.getDecision = async (id) => {
    await new Promise((r) => setTimeout(r, 200));
    const decision = MOCK_DECISIONS.find((d) => d.id === id);
    if (!decision) throw { message: 'Decision not found', code: 'NOT_FOUND', status: 404 };
    return decision;
  };
}
